import { useCallback, useMemo } from 'react';
import type { Region } from '@court-sync/shared';
import { useAuthStore } from '../store/auth';
import {
  useSubscriptionQuery,
  usePlansQuery,
  fetchPlansQuery,
} from '../queries/subscription';
import {
  buildSubscriptionStatusFromResponse,
  getDisplayStatus,
  computeDerivedSubscriptionFlags,
} from '../utils/subscriptionStatus';

/**
 * Hook to access subscription status and plans (React Query).
 */
export function useSubscription(regionOverride?: Region) {
  const authRegion = useAuthStore((s) => s.region);
  const region = regionOverride ?? authRegion;

  const {
    data: subscriptionResponse,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refreshSubscription,
    refreshAfterPayment,
  } = useSubscriptionQuery();

  const {
    data: plans = null,
    isLoading: plansLoading,
    error: plansQueryError,
  } = usePlansQuery(region);

  const fetchPlans = useCallback(
    async (plansRegion?: Region) => {
      return fetchPlansQuery(plansRegion ?? region, { productLine: 'academy' });
    },
    [region]
  );

  const subscriptionStatus = useMemo(() => {
    const base = buildSubscriptionStatusFromResponse(subscriptionResponse ?? null);
    return getDisplayStatus({
      ...base,
      isLoading: subscriptionLoading,
      error:
        subscriptionError instanceof Error
          ? subscriptionError.message
          : subscriptionError
            ? String(subscriptionError)
            : null,
    });
  }, [subscriptionResponse, subscriptionLoading, subscriptionError]);

  const { currentTier, hasMultiLocation } = useMemo(
    () => computeDerivedSubscriptionFlags(subscriptionStatus.subscription, plans),
    [subscriptionStatus.subscription, plans]
  );

  const plansError =
    plansQueryError instanceof Error
      ? plansQueryError.message
      : plansQueryError
        ? String(plansQueryError)
        : null;

  return {
    ...subscriptionStatus,
    plans,
    plansLoading,
    plansError,
    currentTier,
    hasMultiLocation,
    refresh: refreshSubscription,
    refreshAfterPayment,
    fetchPlans,
  };
}
